import React from "react";
import { Text, Flex, Button, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";
import { SPACING } from "#utils/spacing";
import { EmptyStateCard } from "../../../EmptyStateCard";
import * as styles from "./styles_vanilla.css";
import type { WorkflowDocumentsQuery } from "#graphql/supergraphTypes";
import type { StagedDocument } from "../../hooks/useDocuments";

type TaskDocumentAttachment = WorkflowDocumentsQuery["workflowDocuments"][number];

const CardShell: React.FC<{ children: React.ReactNode; testId?: string }> = ({
  children,
  testId,
}) => (
  <Flex
    alignItems="center"
    gap={SPACING.MEDIUM}
    data-testid={testId}
    className={styles.cardShell}
  >
    {children}
  </Flex>
);

const FileIconBadge: React.FC = () => (
  <div className={styles.fileIconBadge}>
    <Icon type="file-alt" color={Colors.Neutral600} size="medium" variant="solid" />
  </div>
);

const AttachedRow: React.FC<{ attachment: TaskDocumentAttachment }> = ({ attachment }) => {
  const doc = attachment.document;
  const [fetchUrl, { loading }] = useDocumentDownloadUrlLazyQuery();

  if (!doc) return null;

  const handleDownload = async () => {
    const { data } = await fetchUrl({ variables: { id: doc.id } });
    const url = data?.documentDownloadUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Download link is unavailable. Please try again later.");
    }
  };

  const date = doc.createdAt
    ? new Date(doc.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <CardShell testId={`document-row-${doc.id}`}>
      <FileIconBadge />
      <Flex flexDirection="column" gap="2px" className={styles.metaColumn}>
        <Text variant="regular-sm" className={styles.fileNameText}>
          {doc.fileName}
        </Text>
        <Text variant="secondary-sm" color={Colors.Neutral500}>
          {date}
          {attachment.taskName ? ` · ${attachment.taskName}` : ""}
        </Text>
      </Flex>
      <Button
        variant="neutral"
        onClick={() => {
          void handleDownload();
        }}
        isLoading={loading}
        disabled={loading}
        data-testid={`document-download-${doc.id}`}
        className={styles.downloadButton}
      >
        Download
      </Button>
    </CardShell>
  );
};

const StagedRow: React.FC<{ doc: StagedDocument }> = ({ doc }) => (
  <CardShell testId={`document-staged-${doc.documentId}`}>
    <FileIconBadge />
    <Flex flexDirection="column" gap="2px" className={styles.metaColumn}>
      <Text variant="regular-sm" className={styles.fileNameText}>
        {doc.fileName}
      </Text>
    </Flex>
    <div className={styles.pendingBadge}>
      <Text variant="secondary-sm" color="#F57F17">
        Pending save
      </Text>
    </div>
  </CardShell>
);

interface DocumentsTabProps {
  documents: TaskDocumentAttachment[];
  stagedDocuments: StagedDocument[];
  loading: boolean;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  stagedDocuments,
  loading,
}) => {
  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="documents-tab-loading">
        <Text variant="secondary-sm" color={Colors.Neutral500}>
          Loading documents…
        </Text>
      </div>
    );
  }

  if (!documents.length && !stagedDocuments.length) {
    return (
      <div className={styles.emptyStateContainer}>
        <EmptyStateCard
          icon="file-alt"
          iconVariant="solid"
          title="No documents to show"
          data-testid="documents-tab-empty"
        />
      </div>
    );
  }

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.SMALL}
      style={{ padding: SPACING.MEDIUM }}
      data-testid="documents-tab"
    >
      {stagedDocuments.map((doc) => (
        <StagedRow key={doc.documentId} doc={doc} />
      ))}
      {documents.map((att) => (
        <AttachedRow key={att.id} attachment={att} />
      ))}
    </Flex>
  );
};

export default DocumentsTab;
